/**
 * Item Lifecycle Management
 *
 * All work objects are Items from creation. No pre-Item stage.
 * lifecycle_state is structural only (e.g. 'item', optionally 'archived'/'discarded').
 * Priority is contextual (list membership + rank), not a lifecycle state.
 * Readiness and queues (lifecycle_stage: intake, shaping, ranking, execution) determine eligibility.
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Structural lifecycle states only (e.g. item, archived, discarded). Priority is contextual.
 */
export type CandidateLifecycleState = 'item' | 'archived' | 'discarded';

/**
 * Queue / stage (derived from readiness), not structural state.
 */
export type LifecycleStage =
  | 'intake'
  | 'triage'
  | 'shaping'
  | 'ranking'
  | 'execution';

/**
 * Status values for idea_submissions table (pre-intake)
 */
export type IdeaSubmissionStatus = 
  | 'INTAKE_PENDING'    // Awaiting initial review
  | 'RETURNED'          // Needs more information from submitter
  | 'SHAPING'           // Being shaped into a candidate
  | 'READY'             // Promoted to candidates table
  | 'REJECTED';         // Will not be pursued

/**
 * Mapping from idea_submissions.status to next actions
 */
export const SUBMISSION_STATUS_ACTIONS: Record<IdeaSubmissionStatus, {
  label: string;
  canPromote: boolean;
  nextStatus?: IdeaSubmissionStatus;
  description: string;
}> = {
  INTAKE_PENDING: {
    label: 'Pending Review',
    canPromote: false,
    nextStatus: 'SHAPING',
    description: 'Awaiting initial triage',
  },
  RETURNED: {
    label: 'Needs Information',
    canPromote: false,
    nextStatus: 'SHAPING',
    description: 'Returned to submitter for clarification',
  },
  SHAPING: {
    label: 'In Item Work',
    canPromote: true,
    nextStatus: 'READY',
    description: 'Converting to structured candidate',
  },
  READY: {
    label: 'Promoted',
    canPromote: false,
    description: 'Now exists in candidates table',
  },
  REJECTED: {
    label: 'Rejected',
    canPromote: false,
    description: 'Will not be pursued',
  },
};

/**
 * Configuration for structural lifecycle states only.
 */
export const LIFECYCLE_STATE_CONFIG: Record<CandidateLifecycleState, {
  label: string;
  description: string;
  allowedStages: LifecycleStage[];
  canTransitionTo: CandidateLifecycleState[];
}> = {
  item: {
    label: 'Item',
    description: 'Work object; queue/stage (intake, item work, ranking) determines flow',
    allowedStages: ['intake', 'triage', 'shaping', 'ranking', 'execution'],
    canTransitionTo: ['archived', 'discarded'],
  },
  archived: {
    label: 'Archived',
    description: 'Retained but not active',
    allowedStages: [],
    canTransitionTo: ['item'],
  },
  discarded: {
    label: 'Discarded',
    description: 'No longer pursued',
    allowedStages: [],
    canTransitionTo: [],
  },
};

// ============================================================================
// Queue View Filters
// ============================================================================

/**
 * Filter configuration for each queue view.
 * Uses lifecycle_stage (and readiness) for flow; lifecycle_state is structural (item).
 */
export const QUEUE_VIEW_FILTERS = {
  triage: {
    table: 'candidates' as const,
    filters: {
      lifecycle_state: 'item' as const,
      lifecycle_stage: ['intake', 'triage'] as const,
    },
    description: 'Items needing initial triage assessment',
  },
  intake: {
    table: 'candidates' as const,
    filters: {
      lifecycle_state: 'item' as const,
      lifecycle_stage: 'intake' as const,
    },
    description: 'Items in intake queue',
  },
  shaping: {
    table: 'candidates' as const,
    filters: {
      lifecycle_state: 'item' as const,
      lifecycle_stage: ['shaping'] as const,
    },
    description: 'Items in item work, gaining attributes',
  },
  candidates: {
    table: 'candidates' as const,
    filters: {
      lifecycle_state: 'item' as const,
    },
    description: 'Items (structural); filter by stage for queue',
  },
  rankingReady: {
    table: 'candidates' as const,
    filters: {
      lifecycle_state: 'item' as const,
      readyForRanking: true,
    },
    description: 'Items with complete attributes ready for prioritization',
  },
  priorities: {
    table: 'candidates' as const,
    filters: {} as { lifecycle_state?: 'item'; itemPoolId?: string },
    description: 'Items in an Item Pool (contextual priority)',
  },
} as const;

// ============================================================================
// Transition Helpers
// ============================================================================

/**
 * Check if a transition is valid
 */
export function canTransition(
  fromState: CandidateLifecycleState,
  toState: CandidateLifecycleState
): boolean {
  return LIFECYCLE_STATE_CONFIG[fromState].canTransitionTo.includes(toState);
}

/**
 * Get the next valid state for a candidate
 */
export function getNextState(
  currentState: CandidateLifecycleState
): CandidateLifecycleState | null {
  const config = LIFECYCLE_STATE_CONFIG[currentState];
  return config.canTransitionTo[0] || null;
}

/**
 * Move item from intake to shaping (stage only; lifecycle_state remains 'item').
 */
export function moveToShaping(_item: { lifecycle_state: string }): {
  lifecycle_stage: 'shaping';
} {
  return { lifecycle_stage: 'shaping' };
}

/**
 * Context for adding an item to a priority list (rank comes from list membership).
 * Callers should add the item to the list's priorityIds; do not set lifecycle_state.
 */
export function getAddToPriorityListContext(
  _item: { lifecycle_state: 'item' },
  itemPoolId: string,
  rank: number
): {
  lifecycle_stage: 'ranking';
  itemPoolId: string;
  rank: number;
} {
  return {
    lifecycle_stage: 'ranking',
    itemPoolId,
    rank,
  };
}

// ============================================================================
// Supabase Query Helpers
// ============================================================================

/**
 * Build a Supabase query for a specific queue view
 * 
 * @example
 * const { table, filters } = QUEUE_VIEW_FILTERS.triage;
 * const query = supabase.from(table).select('*');
 * const filteredQuery = applyLifecycleFilters(query, filters);
 */
export function buildQueueQuery(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing loose row type, not changed here
  supabaseQuery: any,
  queueView: keyof typeof QUEUE_VIEW_FILTERS
) {
  const config = QUEUE_VIEW_FILTERS[queueView];
  let query = supabaseQuery;
  
  // Apply lifecycle_state filter
  if ('lifecycle_state' in config.filters) {
    const states = config.filters.lifecycle_state;
    if (Array.isArray(states)) {
      query = query.in('lifecycle_state', states);
    } else {
      query = query.eq('lifecycle_state', states);
    }
  }
  
  // Apply lifecycle_stage filter if present
  if ('lifecycle_stage' in config.filters) {
    const stages = config.filters.lifecycle_stage;
    if (Array.isArray(stages)) {
      query = query.in('lifecycle_stage', stages);
    } else {
      query = query.eq('lifecycle_stage', stages);
    }
  }
  
  // Apply readyForRanking filter if present
  if ('readyForRanking' in config.filters) {
    query = query.eq('ready_for_ranking', config.filters.readyForRanking);
  }
  
  return query;
}

export default QUEUE_VIEW_FILTERS;
